'use client';

import { c as _c } from "react/compiler-runtime";
import { format } from 'date-fns';
import { m, useSpring, AnimatePresence, type MotionStyle, type MotionValue } from 'motion/react';
import { DURATION, EASE_OUT_STRONG } from '@/lib/motion-utils';
import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import logger from '@/lib/logger';
import { currentYear } from '@/lib/site';
import { calculateTotalSleep, generateYearData, getBarHeight, loadCSVData, processCSVData, VISUALIZATION_MODES, type SleepData } from '@/lib/sleep-data';
import { CenterUnderline } from '@/components/ui/underline-animation';

// ClockDisplay component manages its own time state to avoid re-rendering parent
function ClockDisplay() {
  const $ = _c(14);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Date();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [currentDate, setCurrentDate] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const timer = setInterval(() => setCurrentDate(new Date()), 1000);
      return () => clearInterval(timer);
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== currentDate) {
    t3 = format(currentDate, "HH:mm:ss");
    $[3] = currentDate;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <p className="mb-14 font-mono text-sm text-neutral-500 dark:text-neutral-400">{t3}</p>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== currentDate) {
    t5 = format(currentDate, "MMM d");
    $[7] = currentDate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <p className="mt-14 font-mono text-xs text-neutral-600 dark:text-neutral-500">{t5}</p>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <>{t4}{t6}</>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
}

// Constants hoisted outside component to avoid recreation
const THROTTLE_INTERVAL_MS = 100;
const FIRST_TRACKED_YEAR = 2022;

// Must stay a function: the list runs to the current year, and a module-scope
// array would stop growing until the process restarted.
const availableYears = () => Array.from({
  length: currentYear() - FIRST_TRACKED_YEAR + 1
}, (_, i) => FIRST_TRACKED_YEAR + i).reverse();

/**
 * Resolves one year of sleep data, falling back to generated data when the CSV
 * is missing or unreadable. Never rejects, so callers don't need a try/catch —
 * which also keeps the try/finally the React Compiler bails on out of the
 * component body.
 */
async function loadSleepYear(year: number): Promise<SleepData[]> {
  try {
    const csvData = await loadCSVData();
    if (Array.isArray(csvData) && csvData.length > 0) {
      return processCSVData(csvData, year);
    }
  } catch (error) {
    logger.error({
      error
    }, 'Failed to load CSV data');
  }
  return generateYearData(year);
}
interface ClosestBar {
  element: Element;
  index: number;
  distance: number;
}

// Whether a day has a value for the given visualization mode.
function hasDataForMode(day: SleepData, mode: string): boolean {
  const config = VISUALIZATION_MODES[mode];
  if (!config) return false;
  return day[config.key] !== null;
}
function formatDataValue(day: SleepData, mode: string): string {
  const config = VISUALIZATION_MODES[mode];
  if (!day || !config) return 'No data';
  const value = day[config.key];
  if (value === null || value === undefined) return 'No data';
  return config.format(value as number);
}

// Raw value for the given visualization mode.
function getValueForMode(day: SleepData, mode: string): number | null {
  const config = VISUALIZATION_MODES[mode];
  if (!day || !config) return null;
  return day[config.key] as number ?? null;
}

// Bar color from the mode's value thresholds.
function getBarColor(day: SleepData, mode: string): string {
  const config = VISUALIZATION_MODES[mode];
  if (!day || !config) return '';
  const value = day[config.key] as number | null;
  if (value === null) return 'bg-neutral-300 dark:bg-neutral-800';
  const {
    lowThreshold,
    highThreshold,
    lowerIsBetter
  } = config;
  if (lowerIsBetter) {
    if (value <= highThreshold) return 'bg-green-500';
    if (value >= lowThreshold) return 'bg-red-500';
    return 'bg-yellow-500';
  }
  if (value >= highThreshold) return 'bg-green-500';
  if (value <= lowThreshold) return 'bg-red-500';
  return 'bg-yellow-500';
}

// The bar whose horizontal centre is nearest the pointer, whether or not it
// has data.
function getClosestDayElement(timeline: HTMLDivElement, mouseX: number): ClosestBar | null {
  const bars = timeline.querySelectorAll('#timeline-bars > div');
  if (!bars.length) return null;
  let closestBar: ClosestBar | null = null;
  let minDistance = Infinity;
  bars.forEach((bar, index) => {
    const rect = bar.getBoundingClientRect();
    const barCenter = rect.left + rect.width / 2;
    const distance = Math.abs(mouseX - barCenter);
    if (distance < minDistance) {
      minDistance = distance;
      closestBar = {
        element: bar,
        index,
        distance
      };
    }
  });
  return closestBar;
}

// The pointer counts as "near" the bar strip within a 30px vertical margin.
// Both values are viewport coordinates.
function isNearTimeline(clientY: number, timelineContainer: Element): boolean {
  const timelineRect = timelineContainer.getBoundingClientRect();
  return clientY >= timelineRect.top - 30 && clientY <= timelineRect.bottom + 30;
}

// Plays an audio element from the start, at most once per throttle window.
// Autoplay refusals (NotAllowedError) are expected before the first gesture
// and stay silent.
function playThrottledSound(audioRef: React.RefObject<HTMLAudioElement | null>, lastPlayedRef: React.RefObject<number>, label: string) {
  const now = Date.now();
  if (now - lastPlayedRef.current < THROTTLE_INTERVAL_MS) {
    return; // throttled
  }
  lastPlayedRef.current = now;
  if (audioRef.current) {
    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((e: Error) => {
      if (e.name !== 'NotAllowedError') {
        logger.error({
          errorName: e.name,
          errorMessage: e.message
        }, `${label} sound play failed`);
      }
    });
  }
}
interface SleepTotalsProps {
  isLoading: boolean;
  sleepData: SleepData[];
  selectedYear: number;
  className: string;
}
function SleepTotals(t0) {
  const $ = _c(13);
  const {
    isLoading,
    sleepData,
    selectedYear,
    className
  } = t0;
  let t1;
  if ($[0] !== isLoading || $[1] !== selectedYear || $[2] !== sleepData) {
    t1 = isLoading ? "Calculating sleep..." : `${calculateTotalSleep(sleepData)} hours slept in ${selectedYear}`;
    $[0] = isLoading;
    $[1] = selectedYear;
    $[2] = sleepData;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== className || $[5] !== t1) {
    t2 = <p className={className}>{t1}</p>;
    $[4] = className;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isLoading || $[8] !== selectedYear) {
    t3 = selectedYear === 2022 && !isLoading && <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">Got my Oura ring in August 2022</p>;
    $[7] = isLoading;
    $[8] = selectedYear;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== t2 || $[11] !== t3) {
    t4 = <>{t2}{t3}</>;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
interface TimelineCursorProps {
  isHovering: boolean;
  isPressed: boolean;
  isOverInteractive: boolean;
  x: MotionValue<number>;
  y: MotionValue<number>;
}

// The custom pointer: a dot at rest, a shrunken dot while pressed, a tall line
// while a bar is hovered, hidden over native controls.
function TimelineCursor(t0) {
  const $ = _c(12);
  const {
    isHovering,
    isPressed,
    isOverInteractive,
    x,
    y
  } = t0;
  const t1 = isHovering ? "2px" : isPressed ? "40px" : "48px";
  const t2 = isHovering ? "660px" : isPressed ? "40px" : "48px";
  const t3 = isHovering ? "1px" : "9999px";
  const t4 = isOverInteractive ? 0 : isPressed && !isHovering ? 0.9 : 0.7;
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = {
      "--cursor-width": t1,
      "--cursor-height": t2,
      "--cursor-radius": t3,
      opacity: t4
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      "--cursor-width": {
        duration: DURATION.cursor,
        ease: EASE_OUT_STRONG
      },
      "--cursor-height": {
        duration: DURATION.cursor,
        ease: EASE_OUT_STRONG
      },
      "--cursor-radius": {
        duration: DURATION.cursor,
        ease: EASE_OUT_STRONG
      },
      opacity: {
        duration: 0.2,
        ease: "easeInOut"
      }
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== x || $[7] !== y) {
    t7 = {
      "--cursor-width": "48px",
      "--cursor-height": "48px",
      "--cursor-radius": "9999px",
      width: "var(--cursor-width)",
      height: "var(--cursor-height)",
      borderRadius: "var(--cursor-radius)",
      x,
      y,
      translateX: "-50%",
      translateY: "-50%"
    };
    $[6] = x;
    $[7] = y;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = t7 as MotionStyle & {
    '--cursor-width': string;
    '--cursor-height': string;
    '--cursor-radius': string;
  };
  let t9;
  if ($[9] !== t5 || $[10] !== t8) {
    t9 = <m.div className="pointer-events-none absolute bg-orange-600" animate={t5} transition={t6} style={t8} />;
    $[9] = t5;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}
interface TimelineBarsProps {
  sleepData: SleepData[];
  visualizationMode: string;
}
function TimelineBars(t0) {
  const $ = _c(7);
  const {
    sleepData,
    visualizationMode
  } = t0;
  let t1;
  if ($[0] !== sleepData || $[1] !== visualizationMode) {
    let t2;
    if ($[3] !== visualizationMode) {
      t2 = day => <div key={day.date.toISOString()} className={`w-[1px] ${getBarColor(day, visualizationMode)} ${hasDataForMode(day, visualizationMode) ? "cursor-pointer" : "opacity-30"}`} style={{
        height: getBarHeight(day, visualizationMode, d => getValueForMode(d, visualizationMode))
      }} />;
      $[3] = visualizationMode;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = sleepData.map(t2);
    $[0] = sleepData;
    $[1] = visualizationMode;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== t1) {
    t2 = <div className="flex h-full items-end gap-x-1.5 px-6 sm:px-40" id="timeline-bars">{t1}</div>;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
const useTimelineView = () => {
  const $ = _c(99);
  const [hoveredDay, setHoveredDay] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const timelineRef = useRef(null);
  const [visualizationMode, setVisualizationMode] = useState("sleepScore");
  const [isLoading, setIsLoading] = useState(true);
  const [isOverInteractive, setIsOverInteractive] = useState(false);
  const [selectedYear, setSelectedYear] = useState(_temp);
  const [isYearSelectorOpen, setIsYearSelectorOpen] = useState(false);
  const lastHoveredIndexRef = useRef(null);
  const yearSelectorRef = useRef(null);
  const lineTimeoutRef = useRef(null);
  const circleTimeoutRef = useRef(null);
  const clickSoundRef = useRef(null);
  const popSoundRef = useRef(null);
  const lastClickSoundTime = useRef(0);
  const lastPopSoundTime = useRef(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      stiffness: 300,
      damping: 30
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const x = useSpring(0, t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      stiffness: 300,
      damping: 30
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const y = useSpring(0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (lineTimeoutRef.current) {
        clearTimeout(lineTimeoutRef.current);
        lineTimeoutRef.current = null;
      }
      if (circleTimeoutRef.current) {
        clearTimeout(circleTimeoutRef.current);
        circleTimeoutRef.current = null;
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const clearTimeouts = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => playThrottledSound(clickSoundRef, lastClickSoundTime, "Click");
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const playClickSound = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => playThrottledSound(popSoundRef, lastPopSoundTime, "Pop");
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const playPopSound = t4;
  let t5;
  if ($[5] !== selectedYear) {
    t5 = () => generateYearData(selectedYear);
    $[5] = selectedYear;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const [sleepData, setSleepData] = useState(t5);
  let t6;
  let t7;
  if ($[7] !== selectedYear) {
    t6 = () => {
      let ignore = false;
      setIsLoading(true);
      loadSleepYear(selectedYear).then(data => {
        if (ignore) {
          return;
        }
        setSleepData(data);
        setIsLoading(false);
      }, error => {
        if (ignore) {
          return;
        }
        logger.error({
          error
        }, "Failed to load sleep data");
        setIsLoading(false);
      });
      return () => {
        ignore = true;
      };
    };
    t7 = [selectedYear];
    $[7] = selectedYear;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      clearTimeouts();
      setHoveredDay(null);
      setIsHovering(false);
      lastHoveredIndexRef.current = null;
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleDayLeave = t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setIsPressed(true);
      playPopSound();
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const handleMouseDown = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setIsPressed(false);
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const handleMouseUp = t10;
  let t11;
  if ($[13] !== isHovering || $[14] !== x) {
    t11 = (day, closestBar, rect) => {
      const barIndex = closestBar.index;
      const isDifferentDay = lastHoveredIndexRef.current !== null && lastHoveredIndexRef.current !== barIndex;
      lastHoveredIndexRef.current = barIndex;
      if (!isHovering) {
        clearTimeouts();
        setHoveredDay(day);
        setIsHovering(true);
      } else {
        if (isDifferentDay) {
          playClickSound();
          setHoveredDay(day);
          clearTimeouts();
        }
      }
      const barRect = closestBar.element.getBoundingClientRect();
      const barCenterX = barRect.left + barRect.width / 2 - rect.left;
      x.set(barCenterX);
      clearTimeouts();
    };
    $[13] = isHovering;
    $[14] = x;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const hoverBar = t11;
  let t12;
  if ($[16] !== hoverBar || $[17] !== isHovering || $[18] !== sleepData || $[19] !== visualizationMode) {
    t12 = (mouseX, rect_0) => {
      const timeline = timelineRef.current;
      const closestBar_0 = timeline ? getClosestDayElement(timeline, mouseX) : null;
      const day_0 = closestBar_0 && closestBar_0.index >= 0 && closestBar_0.index < sleepData.length ? sleepData[closestBar_0.index] : null;
      if (closestBar_0 && day_0 && hasDataForMode(day_0, visualizationMode)) {
        hoverBar(day_0, closestBar_0, rect_0);
        return;
      }
      if (isHovering) {
        setHoveredDay(null);
        setIsHovering(false);
        lastHoveredIndexRef.current = null;
      }
    };
    $[16] = hoverBar;
    $[17] = isHovering;
    $[18] = sleepData;
    $[19] = visualizationMode;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const handleMoveNearTimeline = t12;
  let t13;
  if ($[21] !== handleMoveNearTimeline || $[22] !== isHovering || $[23] !== x || $[24] !== y) {
    t13 = e => {
      const timeline_0 = timelineRef.current;
      if (!timeline_0) {
        return;
      }
      const rect_1 = timeline_0.getBoundingClientRect();
      const mouseX_0 = e.clientX;
      const mouseY = e.clientY - rect_1.top;
      x.set(e.clientX - rect_1.left);
      y.set(mouseY);
      const timelineContainer = timeline_0.querySelector(".flex.items-center.w-full.h-64");
      if (timelineContainer && isNearTimeline(e.clientY, timelineContainer)) {
        handleMoveNearTimeline(mouseX_0, rect_1);
        return;
      }
      if (isHovering) {
        clearTimeouts();
        circleTimeoutRef.current = setTimeout(() => {
          if (isHovering) {
            setHoveredDay(null);
            setIsHovering(false);
            lastHoveredIndexRef.current = null;
          }
        }, 200);
      }
    };
    $[21] = handleMoveNearTimeline;
    $[22] = isHovering;
    $[23] = x;
    $[24] = y;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleMouseMove = t13;
  let t14;
  let t15;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => () => {
      if (lineTimeoutRef.current) {
        clearTimeout(lineTimeoutRef.current);
      }
      if (circleTimeoutRef.current) {
        clearTimeout(circleTimeoutRef.current);
      }
    };
    t15 = [];
    $[26] = t14;
    $[27] = t15;
  } else {
    t14 = $[26];
    t15 = $[27];
  }
  useEffect(t14, t15);
  let t16;
  let t17;
  if ($[28] !== isYearSelectorOpen) {
    t16 = () => {
      const handleClickOutside = event => {
        if (yearSelectorRef.current && !yearSelectorRef.current.contains(event.target as Node)) {
          setIsYearSelectorOpen(false);
        }
      };
      if (isYearSelectorOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    };
    t17 = [isYearSelectorOpen];
    $[28] = isYearSelectorOpen;
    $[29] = t16;
    $[30] = t17;
  } else {
    t16 = $[29];
    t17 = $[30];
  }
  useEffect(t16, t17);
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <audio ref={clickSoundRef} src="/sound/click.mp3" preload="auto" aria-label="Timeline click sound effect"><track kind="captions" /></audio>;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <audio ref={popSoundRef} src="/sound/pop.mp3" preload="auto" aria-label="Timeline pop sound effect"><track kind="captions" /></audio>;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== isLoading || $[34] !== selectedYear || $[35] !== sleepData) {
    t20 = <div className="absolute top-4 left-4 z-10 md:hidden"><SleepTotals isLoading={isLoading} sleepData={sleepData} selectedYear={selectedYear} className="text-sm" /></div>;
    $[33] = isLoading;
    $[34] = selectedYear;
    $[35] = sleepData;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="absolute right-4 bottom-28 left-4 z-10 text-center md:hidden"><p className="text-lg font-bold"><CenterUnderline label="Life is more beautiful after a good night's sleep" onClick={_temp2} className="cursor-pointer" /></p></div>;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <p className="text-2xl font-bold"><CenterUnderline label="Life is more beautiful after a good night's sleep" onClick={_temp3} className="cursor-pointer" /></p>;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== isLoading || $[40] !== selectedYear || $[41] !== sleepData) {
    t23 = <div className="absolute top-4 left-4 z-10 hidden md:block">{t22}<SleepTotals isLoading={isLoading} sleepData={sleepData} selectedYear={selectedYear} className="mt-1 text-base" /></div>;
    $[39] = isLoading;
    $[40] = selectedYear;
    $[41] = sleepData;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  let t25;
  let t26;
  let t27;
  let t28;
  let t29;
  let t30;
  let t31;
  let t32;
  let t33;
  let t34;
  let t35;
  let t36;
  let t37;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = e_0 => setVisualizationMode(e_0.target.value);
    t25 = () => setIsOverInteractive(true);
    t26 = () => setIsOverInteractive(false);
    t27 = <option value="sleepScore">Sleep Score</option>;
    t28 = <option value="readinessScore">Readiness Score</option>;
    t29 = <option value="hrv">HRV (ms)</option>;
    t30 = <option value="restingHeartRate">Resting HR (bpm)</option>;
    t31 = <option value="deepSleep">Deep Sleep %</option>;
    t32 = <option value="remSleep">REM Sleep %</option>;
    t33 = <option value="totalSleep">Total Sleep (h)</option>;
    t34 = <option value="sleepDebt">Sleep Debt (h)</option>;
    t35 = <option value="sleepEfficiency">Sleep Efficiency %</option>;
    t36 = <option value="steps">Steps</option>;
    t37 = <option value="activityScore">Activity Score</option>;
    $[43] = t24;
    $[44] = t25;
    $[45] = t26;
    $[46] = t27;
    $[47] = t28;
    $[48] = t29;
    $[49] = t30;
    $[50] = t31;
    $[51] = t32;
    $[52] = t33;
    $[53] = t34;
    $[54] = t35;
    $[55] = t36;
    $[56] = t37;
  } else {
    t24 = $[43];
    t25 = $[44];
    t26 = $[45];
    t27 = $[46];
    t28 = $[47];
    t29 = $[48];
    t30 = $[49];
    t31 = $[50];
    t32 = $[51];
    t33 = $[52];
    t34 = $[53];
    t35 = $[54];
    t36 = $[55];
    t37 = $[56];
  }
  let t38;
  if ($[57] !== visualizationMode) {
    t38 = <select aria-label="Select visualization metric" className="cursor-pointer appearance-none rounded-md border border-neutral-300 bg-white p-2 pr-8 text-sm md:text-base dark:border-neutral-700 dark:bg-neutral-800" value={visualizationMode} onChange={t24} onMouseEnter={t25} onMouseLeave={t26}>{t27}{t28}{t29}{t30}{t31}{t32}{t33}{t34}{t35}{t36}{t37}</select>;
    $[57] = visualizationMode;
    $[58] = t38;
  } else {
    t38 = $[58];
  }
  let t39;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-neutral-400" />;
    $[59] = t39;
  } else {
    t39 = $[59];
  }
  let t40;
  if ($[60] !== t38) {
    t40 = <div className="relative">{t38}{t39}</div>;
    $[60] = t38;
    $[61] = t40;
  } else {
    t40 = $[61];
  }
  const t41 = `Select year, currently ${selectedYear}`;
  let t42;
  if ($[62] !== isYearSelectorOpen) {
    t42 = () => setIsYearSelectorOpen(!isYearSelectorOpen);
    $[62] = isYearSelectorOpen;
    $[63] = t42;
  } else {
    t42 = $[63];
  }
  let t43;
  let t44;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = () => setIsOverInteractive(true);
    t44 = () => setIsOverInteractive(false);
    $[64] = t43;
    $[65] = t44;
  } else {
    t43 = $[64];
    t44 = $[65];
  }
  let t45;
  if ($[66] !== isYearSelectorOpen || $[67] !== selectedYear || $[68] !== t41 || $[69] !== t42) {
    t45 = <button type="button" aria-label={t41} aria-expanded={isYearSelectorOpen} className="flex w-full items-center justify-end pr-6 text-base font-medium text-neutral-500 transition-colors hover:text-neutral-800 md:text-lg dark:text-neutral-400 dark:hover:text-white" onClick={t42} onMouseEnter={t43} onMouseLeave={t44}>{selectedYear}</button>;
    $[66] = isYearSelectorOpen;
    $[67] = selectedYear;
    $[68] = t41;
    $[69] = t42;
    $[70] = t45;
  } else {
    t45 = $[70];
  }
  let t46;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = <ChevronDown className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-neutral-500 dark:text-neutral-400" />;
    $[71] = t46;
  } else {
    t46 = $[71];
  }
  let t47;
  if ($[72] !== isYearSelectorOpen || $[73] !== selectedYear) {
    t47 = isYearSelectorOpen && <m.div initial={{
      opacity: 0,
      x: -10
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -10
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} className="gap-x-reverse absolute right-0 mt-2 flex flex-row-reverse gap-x-2" onMouseEnter={() => setIsOverInteractive(true)} onMouseLeave={() => setIsOverInteractive(false)}>{availableYears().reduce((years, year) => {
        if (year !== selectedYear) {
          years.push(year);
        }
        return years;
      }, []).map((year_0, index) => <m.button key={year_0} initial={{
        opacity: 0,
        y: -5
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: index * 0.03
      }} className="text-sm text-neutral-500 transition-colors hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-white" onClick={() => {
        setSelectedYear(year_0);
        setIsYearSelectorOpen(false);
      }}>{year_0}</m.button>)}</m.div>;
    $[72] = isYearSelectorOpen;
    $[73] = selectedYear;
    $[74] = t47;
  } else {
    t47 = $[74];
  }
  let t48;
  if ($[75] !== t47) {
    t48 = <AnimatePresence>{t47}</AnimatePresence>;
    $[75] = t47;
    $[76] = t48;
  } else {
    t48 = $[76];
  }
  let t49;
  if ($[77] !== t45 || $[78] !== t48) {
    t49 = <div className="relative" ref={yearSelectorRef}>{t45}{t46}{t48}</div>;
    $[77] = t45;
    $[78] = t48;
    $[79] = t49;
  } else {
    t49 = $[79];
  }
  let t50;
  if ($[80] !== t40 || $[81] !== t49) {
    t50 = <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-y-2">{t40}{t49}</div>;
    $[80] = t40;
    $[81] = t49;
    $[82] = t50;
  } else {
    t50 = $[82];
  }
  let t51;
  if ($[83] !== hoveredDay || $[84] !== isHovering || $[85] !== isLoading || $[86] !== isOverInteractive || $[87] !== isPressed || $[88] !== sleepData || $[89] !== visualizationMode || $[90] !== x || $[91] !== y) {
    t51 = isLoading ? <div className="absolute inset-0 flex items-center justify-center"><p className="text-xl">Loading data…</p></div> : <><TimelineCursor isHovering={isHovering} isPressed={isPressed} isOverInteractive={isOverInteractive} x={x} y={y} />{!isHovering && !isOverInteractive && <m.div className="pointer-events-none absolute flex flex-col items-center justify-center" style={{
        x,
        y,
        translateX: "-50%",
        translateY: "-50%"
      }}><ClockDisplay /></m.div>}<div className="absolute inset-0 flex items-center justify-center"><div className="flex h-64 w-full items-center overflow-x-auto overflow-y-hidden" style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none"
        }}><TimelineBars sleepData={sleepData} visualizationMode={visualizationMode} /></div></div>{hoveredDay && <m.div key="hover-tooltip" className="absolute rounded-lg bg-neutral-200 p-2 text-center shadow-lg dark:bg-neutral-800" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3
      }} style={{
        x,
        top: "60%",
        translateX: "-50%"
      }}><p className="text-sm">{format(hoveredDay.date, "MMMM d, yyyy")}</p><p className="text-sm font-semibold">{formatDataValue(hoveredDay, visualizationMode)}</p></m.div>}</>;
    $[83] = hoveredDay;
    $[84] = isHovering;
    $[85] = isLoading;
    $[86] = isOverInteractive;
    $[87] = isPressed;
    $[88] = sleepData;
    $[89] = visualizationMode;
    $[90] = x;
    $[91] = y;
    $[92] = t51;
  } else {
    t51 = $[92];
  }
  let t52;
  if ($[93] !== handleMouseMove || $[94] !== t20 || $[95] !== t23 || $[96] !== t50 || $[97] !== t51) {
    t52 = <div className="relative h-screen w-full cursor-none overflow-hidden bg-[#FCFCFC] text-neutral-800 dark:bg-neutral-900 dark:text-white" onMouseMove={handleMouseMove} onMouseLeave={handleDayLeave} onMouseDown={handleMouseDown} onMouseUp={handleMouseUp} role="application" ref={timelineRef}>{t18}{t19}{t20}{t21}{t23}{t50}{t51}</div>;
    $[93] = handleMouseMove;
    $[94] = t20;
    $[95] = t23;
    $[96] = t50;
    $[97] = t51;
    $[98] = t52;
  } else {
    t52 = $[98];
  }
  return t52;
};
export default function Timeline() {
  return useTimelineView();
}
function _temp() {
  return currentYear();
}
function _temp2() {
  return window.open("https://x.com/bryan_johnson/status/1846626922392179036", "_blank");
}
function _temp3() {
  return window.open("https://x.com/bryan_johnson/status/1846626922392179036", "_blank");
}